'use client';

import { c as _c } from "react/compiler-runtime";
import { useAuth, useWallets } from '@morfi/backend-sdk-v2/client';
import { useTranslations } from 'next-intl';
import type { MouseEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { UserAvatar } from '@/components/images/user-avatar';
import { ACCOUNT_PILL_CLASS, AccountPillBody } from '@/components/ui/account-pill';
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/app-dropdown-menu';
import { useUserPortfolioSummary } from '@/hooks/positions/user-portfolio/selectors';
import { useMediaQuery } from '@/hooks/ui/use-media-query';
import { Link, usePathname } from '@/i18n/navigation';
import { useFormattedCurrency } from '@/lib/currency';
import { useMultiWalletFeature } from '@/providers/multi-wallet-feature-provider';
import { WalletSectionContent } from './wallet-section-content';

/**
 * How long (ms) to wait after the pointer leaves the trigger or content
 * before closing. Short enough to feel responsive; long enough to cross the gap to content.
 */
const CLOSE_DELAY_MS = 120;

/**
 * On touch, ignore "pointer down outside" for this long after open so the
 * opening tap is not mistaken for an outside tap (Radix can fire after content mounts).
 */
const IGNORE_OUTSIDE_AFTER_OPEN_MS = 400;
function preventAutoFocus(e: Event): void {
  e.preventDefault();
}

/**
 * Manages hover-to-open state for the dropdown.
 *
 * - `onTriggerEnter`: open immediately, cancel any pending close.
 * - `onTriggerLeave` / `onContentLeave`: start close timer (desktop only; no-op when coarse pointer).
 * - `onContentEnter`: cancel close timer (pointer crossed the gap).
 *
 * When the primary pointer is coarse (touch), we never close on leave so the
 * menu stays open until the user taps outside or selects an item.
 *
 * Uses `modal={false}` on the DropdownMenu to prevent Radix's focus trap
 * overlay from intercepting pointer events and causing flicker.
 */
function useHoverDropdown() {
  const $ = _c(16);
  const [open, setOpen] = useState(false);
  const timerRef = useRef(null);
  const isCoarsePointer = useMediaQuery("(pointer: coarse)");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = function clearTimer() {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const clearTimer = t0;
  let t1;
  if ($[1] !== isCoarsePointer) {
    t1 = function startCloseTimer() {
      if (isCoarsePointer) {
        return;
      }
      clearTimer();
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        setOpen(false);
      }, CLOSE_DELAY_MS);
    };
    $[1] = isCoarsePointer;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const startCloseTimer = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = function onTriggerEnter() {
      clearTimer();
      setOpen(true);
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const onTriggerEnter = t2;
  let t3;
  if ($[4] !== startCloseTimer) {
    t3 = function onTriggerLeave() {
      startCloseTimer();
    };
    $[4] = startCloseTimer;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const onTriggerLeave = t3;
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = function onContentEnter() {
      clearTimer();
    };
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const onContentEnter = t4;
  let t5;
  if ($[7] !== startCloseTimer) {
    t5 = function onContentLeave() {
      startCloseTimer();
    };
    $[7] = startCloseTimer;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const onContentLeave = t5;
  let t6;
  let t7;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
    t7 = [];
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  useEffect(t6, t7);
  let t8;
  if ($[11] !== isCoarsePointer || $[12] !== onContentLeave || $[13] !== onTriggerLeave || $[14] !== open) {
    t8 = {
      open,
      setOpen,
      isCoarsePointer,
      onTriggerEnter,
      onTriggerLeave,
      onContentEnter,
      onContentLeave
    };
    $[11] = isCoarsePointer;
    $[12] = onContentLeave;
    $[13] = onTriggerLeave;
    $[14] = open;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
}
function PillTriggerContent() {
  const $ = _c(19);
  const {
    user
  } = useAuth();
  const {
    wallets
  } = useWallets();
  const {
    enabled: multiWalletsEnabled,
    primaryWallet
  } = useMultiWalletFeature();
  let t0;
  if ($[0] !== multiWalletsEnabled || $[1] !== primaryWallet || $[2] !== wallets) {
    const visibleWallets = multiWalletsEnabled ? wallets : primaryWallet === null ? [] : [primaryWallet];
    t0 = visibleWallets.filter(_temp).map(_temp2);
    $[0] = multiWalletsEnabled;
    $[1] = primaryWallet;
    $[2] = wallets;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const walletAddresses = t0;
  const t1 = user?.id ?? "";
  let t2;
  if ($[4] !== t1 || $[5] !== walletAddresses) {
    t2 = {
      userId: t1,
      wallets: walletAddresses
    };
    $[4] = t1;
    $[5] = walletAddresses;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const {
    totalD6,
    isPending,
    isError
  } = useUserPortfolioSummary(t2);
  const {
    formatD6
  } = useFormattedCurrency();
  const displayTotalD6 = isPending || isError ? null : totalD6;
  let t3;
  if ($[7] !== displayTotalD6 || $[8] !== formatD6) {
    t3 = displayTotalD6 === null ? <span className="text-caption font-medium font-mono tabular-nums text-foreground-brand">{formatD6("0")}</span> : <span className="text-caption font-medium font-mono tabular-nums text-foreground-brand">{formatD6(displayTotalD6.toString())}</span>;
    $[7] = displayTotalD6;
    $[8] = formatD6;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const t4 = user?.id ?? null;
  const t5 = user?.displayName ?? null;
  const t6 = user?.avatarUrl ?? null;
  const t7 = user?.avatarNsfw ?? false;
  const t8 = user?.avatarPreferences?.rasterise ?? false;
  let t9;
  if ($[10] !== t4 || $[11] !== t5 || $[12] !== t6 || $[13] !== t7 || $[14] !== t8) {
    t9 = <UserAvatar size="xs" userId={t4} displayName={t5} avatarUrl={t6} nsfw={t7} rasterise={t8} className="shrink-0" />;
    $[10] = t4;
    $[11] = t5;
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t3 || $[17] !== t9) {
    t10 = <>{t3}{t9}</>;
    $[16] = t3;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  return t10;
}
function _temp2(wallet_0) {
  return wallet_0.address;
}
function _temp(wallet) {
  return wallet.archivedAt === null;
}
export function WalletDropdown() {
  const $ = _c(37);
  const t = useTranslations("header.wallet");
  const {
    user
  } = useAuth();
  const pathname = usePathname();
  const {
    open,
    setOpen,
    isCoarsePointer,
    onTriggerEnter,
    onTriggerLeave,
    onContentEnter,
    onContentLeave
  } = useHoverDropdown();
  const openedAtRef = useRef(0);
  let t0;
  let t1;
  if ($[0] !== open) {
    t0 = () => {
      if (open) {
        openedAtRef.current = Date.now();
      }
    };
    t1 = [open];
    $[0] = open;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[3] !== pathname || $[4] !== setOpen) {
    t2 = () => {
      if (pathname !== undefined) {
        setOpen(false);
      }
    };
    t3 = [pathname, setOpen];
    $[3] = pathname;
    $[4] = setOpen;
    $[5] = t2;
    $[6] = t3;
  } else {
    t2 = $[5];
    t3 = $[6];
  }
  useEffect(t2, t3);
  let t4;
  if ($[7] !== setOpen) {
    t4 = function handleClose() {
      setOpen(false);
    };
    $[7] = setOpen;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const handleClose = t4;
  let t5;
  if ($[9] !== isCoarsePointer) {
    t5 = function handleInteractOutside(e) {
      if (isCoarsePointer && Date.now() - openedAtRef.current < IGNORE_OUTSIDE_AFTER_OPEN_MS) {
        e.preventDefault();
      }
    };
    $[9] = isCoarsePointer;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleInteractOutside = t5;
  let t6;
  if ($[11] !== isCoarsePointer || $[12] !== setOpen) {
    t6 = function handleOpenChange(next) {
      if (!isCoarsePointer && next === true) {
        return;
      }
      setOpen(next);
    };
    $[11] = isCoarsePointer;
    $[12] = setOpen;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const handleOpenChange = t6;
  let t7;
  if ($[14] !== isCoarsePointer) {
    t7 = function handleTriggerClick(e_0) {
      if (isCoarsePointer) {
        e_0.preventDefault();
      }
    };
    $[14] = isCoarsePointer;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const handleTriggerClick = t7;
  const portfolioHref = user?.id !== undefined ? `/portfolio/${user.id}` : "/portfolio";
  let t8;
  if ($[16] !== t) {
    t8 = t("view_profile");
    $[16] = t;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <AccountPillBody><PillTriggerContent /></AccountPillBody>;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] !== handleTriggerClick || $[20] !== onTriggerEnter || $[21] !== onTriggerLeave || $[22] !== portfolioHref || $[23] !== t8) {
    t10 = <DropdownMenuTrigger asChild={true}><Link href={portfolioHref} className={ACCOUNT_PILL_CLASS} aria-label={t8} onPointerEnter={onTriggerEnter} onPointerLeave={onTriggerLeave} onClick={handleTriggerClick}>{t9}</Link></DropdownMenuTrigger>;
    $[19] = handleTriggerClick;
    $[20] = onTriggerEnter;
    $[21] = onTriggerLeave;
    $[22] = portfolioHref;
    $[23] = t8;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== handleClose) {
    t11 = <WalletSectionContent onClose={handleClose} />;
    $[25] = handleClose;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== handleInteractOutside || $[28] !== onContentEnter || $[29] !== onContentLeave || $[30] !== t11) {
    t12 = <DropdownMenuContent align="end" sideOffset={2} className="w-80" onPointerEnter={onContentEnter} onPointerLeave={onContentLeave} onPointerDownOutside={handleInteractOutside} onInteractOutside={handleInteractOutside} onCloseAutoFocus={preventAutoFocus}>{t11}</DropdownMenuContent>;
    $[27] = handleInteractOutside;
    $[28] = onContentEnter;
    $[29] = onContentLeave;
    $[30] = t11;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== handleOpenChange || $[33] !== open || $[34] !== t10 || $[35] !== t12) {
    t13 = <DropdownMenu open={open} onOpenChange={handleOpenChange} modal={false}>{t10}{t12}</DropdownMenu>;
    $[32] = handleOpenChange;
    $[33] = open;
    $[34] = t10;
    $[35] = t12;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  return t13;
}